/*
 * fetch的一些中间件
 * author: kirkzheng
 * time: 2016-05-25
 */

import qs from 'qs';
import dialog from '@/utils/dialog';
import cacheManager from './cache-manager';

export type RequestMethod = 'GET' | 'POST';

export interface RequestData {
  // 服务商相关
  bm_id?: number;
  agency_uid?: number;

  // 广告主相关
  _advertiser_id?: number; //
  account_id?: number;
  uid?: number;

  // token相关
  g_tk?: string;

  [key: string]: any;
}

export interface RequestOptions {
  url: string; // 路径
  type: RequestMethod;

  data: RequestData | string;

  key?: string;

  contentType?: string | boolean; // contentType

  // 超时配置
  timeout?: number;
  ignoreTimeout?: boolean;

  // 使用seadBeacon发送请求
  useSendBeacon?: boolean;

  // 缓存相关
  cache?: any;
  isCache?: boolean;
  cacheMaxAge?: number;
  isCancel?: boolean;

  // 发起请求时页面全局loading
  loadingTip?: string;

  processData?: boolean;
  isShowError: boolean; // 展示error
  startTime: number;
}

export type MiddlewareModel = (params: RequestOptions, response?: any) => [
  (resolve: any) => any, // resolve
  (reject: any) => any, // reject
];

export type MiddlewareSyncModel = (params: RequestOptions, response?: any) => RequestOptions;

/**
 * 使用sendBeacon发送请求
 * @param params
 * @param response
 * @returns
 */
export const useSendBeacon: MiddlewareModel = (params: RequestOptions) => {
  if (params.useSendBeacon) {
    if (navigator.sendBeacon && typeof FormData === 'function') {
      const data = new FormData();
      for (const key of Object.keys(params.data || {})) {
        if (Object.prototype.hasOwnProperty.call(params.data, key)) {
          data.append(key, (params.data as RequestData)[key]);
        }
      }
      navigator.sendBeacon(params.url, data);
    } else {
      params.useSendBeacon = false;
    }
  }

  return [
    (response = {}) => {
      return response;
    },
    (response = {}) => {
      return response;
    },
  ];
};


/**
 * cgi返回码上报
 * @param  {Object} params cgi相关参数
 * @param  {Object} response cgi返回结果
 * @return void
 */
const codeReport = (params: RequestOptions, response: any = {}) => {
  const now = +new Date();
  const cgiUrl = params.url || '';
  let responsetext = {};
  let urlQueryData: any = {};
  let deferTime = 5000;

  // e.qq.com下的返回结果不存data，因为太多了
  responsetext = {
    ret: response.ret,
    msg: response.msg || '',
  };

  // 如果请求的接口没有包含e.qq.com的，那就存全部
  if (params.url.indexOf('e.qq.com') < 0) {
    responsetext = response;
  }

  // 拆分下cgi url的参数
  if (cgiUrl) {
    urlQueryData = qs.parse(cgiUrl.slice(cgiUrl.indexOf('?') + 1));
  }

  // 如果本身是统计接口就不上报了
  if (cgiUrl.indexOf('?mod=monitor&act=rpttdbank') > -1) {
    return false;
  }

  // 如果是紧要的接口，那就立马上报
  if (urlQueryData.mod == 'account' && urlQueryData.act == 'info') {
    deferTime = 0;
  }
};

/*
 * 上报返回码
 * @param fetch
 * @param params {obejct}
 *        params.url 请求的url
 *        params.type 请求的方法 POST/GET
 *        params.data 请求的数据
 * @return fetch
 */
export const responseToReport: MiddlewareModel = (params: RequestOptions) => {
  const startTime = params.startTime || +new Date();

  return [
    (response = {}) => {
      return response;
    },
    (response = {}) => {
      codeReport(params, response);
      return response;
    },
  ];
};

/*
 * 处理下全局的错误码之类的
 * @param fetch
 * @param params {obejct}
 *        params.url 请求的url
 *        params.type 请求的方法 POST/GET
 *        params.data 请求的数据
 * @return fetch
 */
export const responseToRet: MiddlewareModel = (params: RequestOptions) => [
  (response) => {
    // 如果code非0则进行提示
    const { code } = response;
    if (code == 0) {
      return response;
    }
    dialog.handleErrResponse(response);
  },
  (response) => response,
];

/*
 * 处理下全局的错误码之类的
 * @param fetch
 * @param params {obejct}
 *        params.url 请求的url
 *        params.type 请求的方法 POST/GET
 *        params.data 请求的数据
 * @return fetch
 */
export const handleErrResponse: MiddlewareModel = (params: RequestOptions) => {
  const { isShowError = true, loadingTip } = params;

  return [
    (response) => {
      const { code } = response;
      if (loadingTip) {
        dialog.hideLoading();
      }

      if (code !== 0 && isShowError) {
        dialog.handleErrResponse(response);
      }
      // PHP超时
      if (response.data?.code === 110021) {
        const message = '接口请求超时，请联系管理员反馈';
        dialog.handleErrResponse(response, message);
      }
      return response;
    },
    function (response) {
      if (loadingTip) {
        dialog.hideLoading();
      }
      const isAbort = response.statusText === 'abort';
      const isTimeout = response.statusText === 'timeout';
      if (isAbort) {
        return Promise.reject(response);
      }
      // if (isShowError && !ignoreTimeout) {
      //   const message = isTimeout ? '接口请求超时，请联系管理员反馈' : '网络状态异常，请稍后再试！请检查网络链接或防广告插件';
      //   dialog.handleErrResponse(response, message);
      // }
      return response;
    },
  ];
};

// 读取缓存
export const handleGetCache: MiddlewareModel = (params: RequestOptions) => {
  const { data, url, type, isCache } = params;

  return [
    function () {
      const cacheKey = cacheManager.genKey({ ...(data as any), url, type });
      if (isCache && cacheKey) {
        return Promise.resolve({ ...params, cache: cacheManager.get(cacheKey) });
      }
      return Promise.resolve(params);
    },
    function () {
      return Promise.reject();
    },
  ];
};

// 设置缓存
export const handleSetCache: MiddlewareModel = (params: RequestOptions, requestPromise) => {
  const { data, url, type, isCache, cacheMaxAge } = params;
  const key = cacheManager.genKey({ ...(data as any), url, type });

  return [
    function (response) {
      if (isCache) {
        cacheManager.set(key, requestPromise, cacheMaxAge);
      } else if (cacheManager.has(key)) {
        cacheManager.set(key, requestPromise, cacheMaxAge);
      }
      return response;
    },
    function (response) {
      cacheManager.del(key);
      return response;
    },
  ];
};

// 展示页面全局loading
export const handleRequestLoading: MiddlewareModel = (params: RequestOptions) => {
  const { loadingTip = '' } = params;

  return [
    function () {
      if (loadingTip) {
        dialog.showLoading(loadingTip);
      }
      return Promise.resolve(params);
    },
    function () {
      return Promise.reject();
    },
  ];
};

/**
 * 获取请求缓存
 * @param {*} params
 * @returns
 */
export const handleGetCacheSync: MiddlewareSyncModel = (params: RequestOptions) => {
  const { data, url, type, isCache } = params;
  const cacheKey = cacheManager.genKey({ ...(data as any), url, type });
  if (isCache && cacheKey) {
    return { ...params, cache: cacheManager.get(cacheKey) };
  }
  return params;
};

/**
 * 设置请求缓存
 * @param {*} params
 * @param {*} requestPromise
 * @returns
 */
export const handleSetCacheSync: MiddlewareSyncModel = (params, requestPromise) => {
  const { data, url, type, isCache, cacheMaxAge } = params;
  const key = cacheManager.genKey({ ...(data as any), url, type });
  if (isCache && key) {
    cacheManager.set(key, requestPromise, cacheMaxAge);
  }

  return params;
};

/**
 * 更新请求缓存
 * @param {*} params
 * @returns
 */
export const handleUpdateCache: MiddlewareModel = (params) => {
  const { data, url, type, isCache } = params;
  const key = cacheManager.genKey({ ...(data as any), url, type });
  return [
    (response) => {
      return response;
    },
    (response) => {
      if (isCache && key) {
        cacheManager.del(key);
      }
      return response;
    },
  ];
};
